import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { uploadFile, getFiles, deleteFile } from '../firebase/storage';
import { createOrUpdateUser } from '../firebase/users';
import { logoutUser } from '../firebase/auth';
import UserSettings from './UserSettings'; // Import UserSettings component
import FileUploader from './FileUploader';
import FileList from './FileList';
import CommentCitationSystem from './CommentCitationSystem';

const FileUploadSystem = ({ user: propUser }) => {
  const [user, setUser] = useState(propUser || null);
  const [userData, setUserData] = useState(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('files');
  const [showSettings, setShowSettings] = useState(false); // New state for settings modal
  
  const auth = getAuth();
  
  // Handle auth state changes (if not provided via props)
  useEffect(() => {
    if (propUser) return; // Skip if user is provided via props
    
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);
        try {
          const firestoreUser = await createOrUpdateUser(authUser);
          setUserData(firestoreUser);
          loadFiles();
        } catch (err) {
          console.error("Error setting up user:", err);
          setError("Failed to set up user profile. Some features may be limited.");
        }
      } else {
        setUser(null);
        setUserData(null);
        setFiles([]);
        setIsLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, [auth, propUser]);
  
  // Load user data if provided via props
  useEffect(() => {
    if (propUser && !userData) {
      const loadUserData = async () => {
        try {
          const firestoreUser = await createOrUpdateUser(propUser);
          setUserData(firestoreUser);
          loadFiles();
        } catch (err) {
          console.error("Error setting up user:", err);
          setError("Failed to set up user profile. Some features may be limited.");
        }
      };
      
      loadUserData();
    }
  }, [propUser, userData]);
  
  // Load files
  const loadFiles = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const filesData = await getFiles(user.uid);
      setFiles(filesData);
    } catch (err) {
      console.error("Error loading files:", err);
      setError("Failed to load files. Please refresh the page to try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!user) return;
    
    try {
      await uploadFile(file, user.uid);
      loadFiles(); // Refresh the file list
    } catch (err) {
      console.error("Error uploading file:", err);
      setError("Failed to upload file. Please try again.");
    }
  };
  
  // Handle file deletion
  const handleFileDelete = async (fileId) => {
    if (!user) return;
    
    try {
      await deleteFile(fileId, user.uid);
      loadFiles(); // Refresh the file list
    } catch (err) {
      console.error("Error deleting file:", err);
      setError("Failed to delete file. Please try again.");
    }
  };
  
  // Handle user sign in

  // Handle user sign out
  const handleSignOut = async () => {
    try {
      await logoutUser();
    } catch (err) {
      console.error("Error signing out:", err);
      setError("Failed to sign out. Please try again.");
    }
  };
  
  // Toggle settings modal
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };
  
  return (
    <div className="file-upload-system">
      {error && (
        <div className="error-banner">
          {error}
          <button 
            className="close-button" 
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Auth section */}
      <div className="auth-section">
        <div className="user-info">
          <div className="user-details">
            {user.photoURL && (
              <img src={user.photoURL} alt={user.displayName} className="user-avatar" />
            )}
            <div>
              <p className="user-name">{user?.displayName || user?.email}</p>
              <p className="user-email">{user?.email}</p>
              {userData?.title && <p className="user-title">{userData.title}</p>}
            </div>
          </div>
          <div className="user-actions">
            <button 
              onClick={toggleSettings}
              className="settings-button"
              title="User Settings"
            ></button>
            </div>
          </div>
          <button 
            onClick={handleSignOut}
            className="sign-out-button"
          >
            Sign Out
          </button>
        </div>
      
      
      {/* Tabs section */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          Files
        </button>
        <button 
          className={`tab ${activeTab === 'comments' ? 'active' : ''}`}
          onClick={() => setActiveTab('comments')}
        >
          Comments & Citations
        </button>
      </div>
      
      {/* Content based on active tab */}
      <div className="tab-content">
        {activeTab === 'files' ? (
          <div className="files-section">
            <FileUploader onFileUpload={handleFileUpload} />
            <FileList 
              files={files} 
              onDeleteFile={handleFileDelete} 
              isLoading={isLoading}
              isAuthenticated={true}
            />
          </div>
        ) : (
          <CommentCitationSystem user={user} userData={userData} />
        )}
      </div>
    </div>
  );
};

export default FileUploadSystem;